/**
 * The messages the customer gets about their appointments — one per milestone, each
 * filled from that sale's own details.
 *
 * (user 2026-10-05: "the share whatsapp shd be beside those milestone that has watsapp
 * message. so each of milestone we will preset a message, that leads to the phone
 * number. using their name, details ... follow each sales details")
 *
 * ONE definition, shared by the sale page's Project Timeline card and the Scheduling
 * board, because the same appointment sent from two screens has to read the same.
 *
 * The INSTALLATION text is the user's own wording, kept exactly — line breaks, the full
 * stops, the numbered notes and the hotline. The other three are first drafts built in
 * the same shape and marked PROVISIONAL below; replacing one means editing its `lines`
 * function and nothing else.
 */

/**
 * The only panel the quotation prints, so the only one a message can name. Confirmed with
 * the user (2026-10-05: "no currently") that no sale is any other brand today — if one
 * ever is, it needs recording on the sale, and this constant is what reads it.
 */
const PANEL_BRAND = 'TRINA';

const AFTER_SALES_HOTLINE = '+60 19-632 3358';

/**
 * When the crew turns up, written after the installation date:
 *     Installation : 26-Jun-26 (9am-10.30am Team Arrival)
 *
 * One line for every job, because that is the standard slot. (user 2026-10-05, who had
 * been typing it in by hand: "i put the team arrival time" ... "yes by default will do")
 *
 * When a job needs its own window this is where a per-sale value would plug in — it needs
 * somewhere to live first (a nullable `sales.ArrivalWindow`), and that is a schema change
 * for the user to run, so it is deliberately not pretended at here.
 */
const TEAM_ARRIVAL = '9am-10.30am Team Arrival';
// The delivery window, same shape as the arrival one above and for the same reason: the
// customer has to know whether to be home for a morning or a whole day. SOP v58 puts it in
// writing at 5.6 and 8.1, so the message says it too rather than leaving the crew to.
// (user 2026-10-06: "delivery time (830am - 1130am) can add to whatsapp")
const DELIVERY_WINDOW = '8.30am-11.30am';
// The payment deadline, and what happens if it passes. A date with no deadline behind it
// is a date that slips: the crew, the lorry and the stock are all booked off the back of
// it, which is why the slot cannot be held open on a promise. Friday is the SOP's own
// cut-off (step 6 asks "60% paid by Friday?"), so the message and the process say the
// same day. (user 2026-10-07: "i wanna adjust the payment b4 installation. Need to set
// the deadline if not will reschedule")
/**
 * The Friday before an installation date — the day the 60% has to be in.
 *
 * "Friday" on its own makes the customer work out which Friday, and they will pick the
 * wrong one. This returns the LAST Friday strictly before the installation, so an
 * installation that falls on a Friday points at the Friday a week earlier rather than at
 * the morning the crew arrives.
 *
 * Returns null when there is no installation date, and the line then falls back to the
 * plain word — a message never invents a deadline it cannot date.
 * (user 2026-10-07: "Can put the Friday date? Friday before installation date")
 */
const paymentFriday = (installationOn) => {
    if (!installationOn) return null;
    const d = new Date(installationOn);
    if (isNaN(d.getTime())) return null;
    // Back up to the previous Friday; a Friday install goes back a full week, never 0 days.
    const back = ((d.getDay() - 5 + 7) % 7) || 7;
    d.setDate(d.getDate() - back);
    return d;
};

/** "before Friday, 2-Oct-26", or just "before Friday" when there is no date to name. */
const paymentDeadline = (j) => {
    const f = paymentFriday(j?.installationOn);
    return f ? `before Friday, ${msgDate(f)}` : 'before Friday';
};

/**
 * How much of the total has to be in before the crew rolls; the rest follows the
 * installation. (user 2026-10-05: "Payment 60% of total payment is required before
 * installation and remaining balance after installation process")
 *
 * The Scheduling board flags against the same figure, and the bell rings on it — this is
 * the sentence the customer was sent, so the three have to agree.
 */
export const PRE_INSTALL_PCT = 60;

/* ── the bits of a sale a message talks about ─────────────────────────────── */

/** "8-Oct-26" — the format the user writes dates in to customers. */
export const msgDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    const mon = d.toLocaleDateString('en-GB', { month: 'short' });
    return `${d.getDate()}-${mon}-${String(d.getFullYear()).slice(2)}`;
};

/** Local form, digits only: 018-323 8481 and +60183238481 both read 0183238481. */
const localPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) return '';
    return digits.startsWith('60') ? '0' + digits.slice(2) : digits;
};

const joinAddress = (...parts) => parts.filter(Boolean).join(', ');

/**
 * Everything a message can quote, in one shape, so the builders below never have to know
 * whether they are being handed a sale page's object or a board row.
 */
export const jobFromRow = (row) => ({
    customerName: row?.customerName,
    phone: row?.customerPhone,
    address: joinAddress(row?.address, row?.city, row?.state),
    salesPerson: row?.agentAlias || row?.agentName,
    kwp: Number(row?.energy) || 0,
    panelQty: Number(row?.panelQty) || 0,
    panelWatt: Number(row?.panelWatt) || 0,
    batteryQty: Number(row?.batteryQty) || 0,
    siteVisitOn: row?.siteVisitOn,
    deliveryOn: row?.deliveryOn,
    installationOn: row?.installationOn,
    tnbApprovedOn: row?.tnbApprovedOn,
});

/** The same shape, from the sale page's GetSalesById object. */
export const jobFromSale = (sale) => {
    const c = sale?.customerDetails || {};
    const a = sale?.agentDetails || {};
    const panelQty = Number(sale?.panelQty) || 0;
    const kwp = Number(sale?.energy) || 0;
    const batteryQty = (sale?.lineItems || [])
        .filter(li => li.isBattery)
        .reduce((n, li) => n + (Number(li.qty) || 0), 0);
    return {
        customerName: c.customerName,
        phone: c.phoneNumber,
        address: joinAddress(c.address, c.city, c.state),
        salesPerson: a.alias || `${a.firstName || ''} ${a.lastName || ''}`.trim(),
        kwp,
        panelQty,
        // Derived exactly as the API derives it for the board, and as the quotation PDF
        // falls back to: no column stores watts per panel.
        panelWatt: panelQty > 0 && kwp > 0 ? Math.round((kwp * 1000) / panelQty) : 0,
        batteryQty,
        siteVisitOn: sale?.siteVisitOn,
        deliveryOn: sale?.deliveryOn,
        installationOn: sale?.installationOn,
        tnbApprovedOn: sale?.tnbApprovedOn,
    };
};

/** "9.10KWP 14PANELS 1BATTERY" — the battery part only when there is one. */
const systemLine = (j) => {
    const bits = [];
    if (j.kwp) bits.push(`${j.kwp.toFixed(2)}KWP`);
    if (j.panelQty) bits.push(`${j.panelQty}PANELS`);
    if (j.batteryQty) bits.push(`${j.batteryQty}BATTERY`);
    return bits.join(' ');
};

/** The block every appointment message opens with. */
const detailBlock = (j) => {
    const rows = [];
    if (j.customerName) rows.push(`Name:${j.customerName}`);
    if (j.phone) rows.push(`Phone Number: ${localPhone(j.phone)}`);
    if (j.address) rows.push(`Installation Address：${j.address}`);
    if (j.salesPerson) rows.push(`Sales Person: ${j.salesPerson}`);
    const sys = systemLine(j);
    if (sys) rows.push(`KWP：${sys}`);
    if (j.panelWatt) rows.push(`Panel：${PANEL_BRAND} ${j.panelWatt}W`);
    return rows;
};

const confirmBlock = [
    'Kindly confirm the above details and reply with "OK" to confirm.',
    '',
    'If the date is not available, please reply with "No" for reschedule. Thank you',
];

const installNotes = (j) => [
    'Please Take Note:',
    "1.⁠ ⁠For all Gated / Guarded with management we'll 100% need approval from management to install if not we'll reschedule another time.",
    '2.⁠ ⁠The owner must be present on-site to confirm wiring and installation decisions; otherwise, any resulting consequences will be their responsibility.',
    `3.⁠ ⁠Please contact our after-sales service hotline for assistance: [${AFTER_SALES_HOTLINE}].`,
    `4.⁠ ⁠Payment ${PRE_INSTALL_PCT}% of total payment is required ${paymentDeadline(j)} for our scheduling, logistic and material arrangement. The remaining balance is payable after the installation process.`,
    '5.⁠ ⁠If the payment is not received by then, the installation date will be released and rescheduled to the next available slot.',
];

/**
 * The message is from whoever is sending it — the signed-in person, not a fixed name.
 * (user 2026-10-05: "sender is user"). With no name to use at all, it speaks for the team
 * rather than putting somebody else's name on somebody else's message.
 */
const hello = (sender) => (sender
    ? `Hi, Good Day I'm ${sender} from Astern Solar Scheduling Team.`
    : 'Hi, Good Day, this is the Astern Solar Scheduling Team.');

/* ── one entry per milestone ──────────────────────────────────────────────────
   `needs` is the date that must be SAVED before the message means anything — the
   share button stays off until it is, so a message can never promise a date nobody
   recorded. A milestone with no entry here simply has no share button.          */

export const MILESTONE_MESSAGES = {
    // The user's own wording, 2026-10-05. Do not reword without asking.
    installationOn: {
        label: 'Installation',
        needs: 'installationOn',
        lines: (j, sender) => [
            hello(sender),
            '',
            'Please find the details of your installation appointment below:',
            '',
            ...detailBlock(j),
            '',
            'Scheduled Installation',
            ...(msgDate(j.deliveryOn) ? [`Delivery:${msgDate(j.deliveryOn)} (${DELIVERY_WINDOW})`] : []),
            `Installation : ${msgDate(j.installationOn)} (${TEAM_ARRIVAL})`,
            // The money, said where the dates are said, with the day it is needed by —
            // the deadline belongs beside the date it protects. (user 2026-10-05, 2026-10-07)
            `Payment : ${PRE_INSTALL_PCT}% of total payment is required ${paymentDeadline(j)} for our scheduling, logistic and material arrangement.`,
            '',
            ...confirmBlock,
            '',
            'Please kindly share front view if confirmed.',
            '',
            ...installNotes(j),
        ],
    },

    // ON HOLD — a draft in the right shape, NOT offered in the UI, because sending our
    // words to a customer in the user's name is not ours to decide. `draft` is what hides
    // it; delete that one line when the wording arrives. (user 2026-10-05: "the rest on
    // hold will do")
    siteVisitOn: {
        draft: true,
        label: 'Site Visit',
        needs: 'siteVisitOn',
        lines: (j, sender) => [
            hello(sender),
            '',
            'Please find the details of your site visit below:',
            '',
            ...detailBlock(j),
            '',
            'Scheduled Site Visit',
            `Site Visit : ${msgDate(j.siteVisitOn)}`,
            '',
            'Our team needs access to the roof, the meter and the main switchboard, so',
            'someone who can open them should be at home.',
            '',
            ...confirmBlock,
        ],
    },

    // ON HOLD — a draft in the right shape, NOT offered in the UI, because sending our
    // words to a customer in the user's name is not ours to decide. `draft` is what hides
    // it; delete that one line when the wording arrives. (user 2026-10-05: "the rest on
    // hold will do")
    deliveryOn: {
        draft: true,
        label: 'Product Delivery',
        needs: 'deliveryOn',
        lines: (j, sender) => [
            hello(sender),
            '',
            'Please find the details of your delivery below:',
            '',
            ...detailBlock(j),
            '',
            'Scheduled Delivery',
            `Delivery:${msgDate(j.deliveryOn)} (${DELIVERY_WINDOW})`,
            ...(msgDate(j.installationOn) ? [`Installation : ${msgDate(j.installationOn)}`] : []),
            '',
            'Someone needs to be home to receive the delivery, and we will need somewhere',
            'dry to leave the panels and the battery until the installation day.',
            '',
            ...confirmBlock,
        ],
    },

    // ON HOLD — a draft in the right shape, NOT offered in the UI, because sending our
    // words to a customer in the user's name is not ours to decide. `draft` is what hides
    // it; delete that one line when the wording arrives. (user 2026-10-05: "the rest on
    // hold will do")
    tnbApprovedOn: {
        draft: true,
        label: 'TNB Approval',
        needs: 'tnbApprovedOn',
        lines: (j, sender) => [
            hello(sender),
            '',
            'Good news — TNB has approved your solar system.',
            '',
            ...detailBlock(j),
            '',
            `TNB Approval : ${msgDate(j.tnbApprovedOn)}`,
            '',
            'Your system is now running and exporting. Thank you for choosing Astern Solar.',
            '',
            `For anything at all, our after-sales hotline is [${AFTER_SALES_HOTLINE}].`,
        ],
    },
};

/**
 * Is there a message for this milestone that may actually be sent? A draft is not one —
 * it exists so the wording can be dropped in later, and until then the UI shows no button
 * at all rather than one that sends words nobody approved.
 */
export const hasMessage = (key) =>
    Boolean(MILESTONE_MESSAGES[key] && !MILESTONE_MESSAGES[key].draft);

/** Does this milestone have a sendable message, and is its date saved? */
export const canMessage = (key, job) =>
    Boolean(hasMessage(key) && job && job[MILESTONE_MESSAGES[key].needs] && job.phone);

/** The finished text for one milestone, or '' when that milestone has no message. */
export function buildMilestoneMsg(key, job, sender) {
    const m = MILESTONE_MESSAGES[key];
    if (!m || !job) return '';
    return m.lines(job, sender).join('\n');
}
